/**
 * StepNavigation
 *
 * Controls navigation for the multi-step create-campaign flow, with per-step
 * validation, draft saving, AI generate, and template selection helpers.
 */
import React from 'react';
import { ChevronLeft, ChevronRight, Sparkles, Copy, Check, X } from 'lucide-react';
import { ButtonSpinner } from '../../ui/feedback/loading-spinner.jsx';

// Updated validation for each step
const isStepValid = (currentStep, formData, surveyQuestions, isSettingsMode = false) => {
  // If in settings mode, treat the currentStep as already mapped
  // Otherwise, use the original campaign creation flow validation
  
  if (isSettingsMode) {
    switch (currentStep) {
      case 0: // Internal Name: require a name
        return Boolean(formData.name?.trim());
      case 1: // Design: always valid since theme has a default value
        return true;
      case 2: // Basic Info: require title and description
        return Boolean(formData.title?.trim() && formData.description?.trim());
      case 3: // Campaign Details: require at least one non-empty survey question or explainer video
        const hasValidSurveyQuestions = Array.isArray(surveyQuestions) && 
                                      surveyQuestions.length > 0 && 
                                      surveyQuestions.some(q => q.question && q.question.trim());
        const hasExplainerVideo = formData.hasExplainerVideo && formData.explainerVideo;
        
        // If explainer video toggle is on, we need a video uploaded or valid questions
        // If explainer video toggle is off, we just need at least one valid question
        const contentValid = formData.hasExplainerVideo 
          ? hasValidSurveyQuestions || hasExplainerVideo
          : hasValidSurveyQuestions;
        
        return contentValid;
      case 4: // Review: always valid
        return true;
      default:
        return false;
    }
  } else {
    // Campaign creation flow validation (updated for 6-step flow)
    switch (currentStep) {
      case 0: // Introduction: always valid
        return true;
      case 1: // Internal Name: require a name
        return Boolean(formData.name?.trim());
      case 2: // Design: always valid since theme has a default value
        return true;
      case 3: // Basic Info: require title and description
        return Boolean(formData.title?.trim() && formData.description?.trim());
      case 4: // Campaign Details: require at least one non-empty survey question or explainer video
        // Safely handle null/undefined surveyQuestions
        const validSurveyQuestions = Array.isArray(surveyQuestions) ? surveyQuestions : [];
        
        const hasValidSurveyQuestions = validSurveyQuestions.length > 0 && 
                                     validSurveyQuestions.some(q => q && q.question && q.question.trim());
        const hasExplainerVideo = formData.hasExplainerVideo && formData.explainerVideo;
        
        // ALWAYS require at least one valid survey question, regardless of explainer video
        // The explainer video is optional but never a replacement for survey questions
        return hasValidSurveyQuestions;
      case 5: // Review: always valid since it's the last step
        return true;
      default:
        return false;
    }
  }
};

export function StepNavigation({
  className = '',
  currentStep,
  totalSteps,
  onPrevious,
  onNext,
  isSubmitting,
  isSavingDraft,
  handleSaveDraft,
  isEditingDraft,
  formData,
  surveyQuestions,
  setIsAIModalOpen,
  onUseTemplate,
  clearSelectedTemplate,
  selectedTemplate,
  submitButtonText,
  onSubmit,
  isSettingsMode = false,
  canProceed: externalCanProceed = null,
  renderCustomButtons = false
}) {
  // Use provided canProceed if available, otherwise calculate it internally
  const canProceed = externalCanProceed !== null ? externalCanProceed : isStepValid(currentStep, formData, surveyQuestions, isSettingsMode);
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;
  
  // Ensure button is only disabled when needed
  const isButtonDisabled = React.useMemo(() => {
    // Always enable the button on the last step (review page)
    if (isLastStep) {
      return isSubmitting; // Only disable if currently submitting
    }
    
    // For Introduction step (step 0), always enable
    if (currentStep === 0) {
      return isSubmitting;
    }

    // For Internal Name step (step 1), check if name is provided
    if (currentStep === 1) {
      return isSubmitting || !formData.name?.trim();
    }
    
    // For other steps, check if the step is valid
    return isSubmitting || !canProceed;
  }, [currentStep, isSubmitting, canProceed, isLastStep, formData.name]);
  
  // Get the correct button text based on current state
  const getCorrectButtonText = () => {
    if (isSubmitting) {
      return isLastStep ? (submitButtonText ? submitButtonText.replace('Update', 'Updating').replace('Create', 'Creating') : 'Creating...') : 'Next...';
    }
    
    if (isLastStep) {
      return submitButtonText || 'Create Campaign';
    }
    
    // Special case for intro page
    if (currentStep === 0) {
      return 'Get Started';
    }
    
    return 'Next';
  };

  // Function to truncate template title
  const truncateTitle = (title, maxLength = 14) => {
    if (!title) return '';
    return title.length > maxLength ? `${title.substring(0, maxLength)}...` : title;
  };

  // Handle button click based on whether we're on the last step
  const handleButtonClick = (e) => {
    // Always prevent default form submission behavior
    e.preventDefault();
    
    // If this is the last step, call the onSubmit function if provided
    if (isLastStep && onSubmit) {
      onSubmit();
    } else {
      // For all other steps, call onNext to move to the next step
      onNext(e);
    }
  };

  // Render the Template Selection button
  const renderTemplateButton = (isMobile = false) => {
    if (isSettingsMode) return null;
    
    return (
      <button
        type="button"
        onClick={selectedTemplate ? null : onUseTemplate}
        className={`${isMobile ? 'w-full' : ''} inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-primary-text-600 hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:text-primary-text-400 dark:hover:bg-gray-800`}
      >
        {selectedTemplate ? (
          <>
            <Check className="h-4 w-4 flex-shrink-0 text-primary-500" />
            <span className="whitespace-normal text-left">
              {`Using "${truncateTitle(selectedTemplate.title)}"`}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                clearSelectedTemplate && clearSelectedTemplate();
              }}
              className="ml-2 p-0.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Remove template"
              title="Remove template"
            >
              <X className="h-3.5 w-3.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" />
            </button>
          </>
        ) : (
          <>
            <Copy className="h-4 w-4 flex-shrink-0" />
            <span className="whitespace-normal text-left">Use Previous Campaign</span>
          </>
        )}
      </button>
    );
  };

  // Render Previous button
  const renderPreviousButton = (isMobile = false) => {
    return (
      <button
        type="button"
        onClick={onPrevious}
        className={`${isMobile ? 'w-full' : ''} inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800`}
      >
        <ChevronLeft className="h-4 w-4" />
        Previous
      </button>
    );
  };

  // Render loading spinner
  const renderLoadingSpinner = () => (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );

  // Render Next/Submit button
  const renderNextButton = () => {
    return (
      <button
        type={isLastStep ? 'submit' : 'button'}
        onClick={handleButtonClick}
        disabled={isButtonDisabled}
        className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium ${
          isButtonDisabled 
            ? 'bg-gray-400 cursor-not-allowed dark:bg-gray-600 text-gray-100 dark:text-gray-300' 
            : 'bg-primary-600 text-white hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600'
        }`}
      >
        {isSubmitting ? (
          <>
            {renderLoadingSpinner()}
            {getCorrectButtonText()}
          </>
        ) : (
          <>
            {getCorrectButtonText()}
            {!isLastStep && <ChevronRight className="h-4 w-4" />}
          </>
        )}
      </button>
    );
  };

  // Render Save Draft button
  const renderSaveDraftButton = () => {
    if (isSettingsMode) return null;
    
    return (
      <button
        type="button"
        onClick={handleSaveDraft}
        disabled={isSavingDraft || !formData.name?.trim() || !formData.title?.trim() || !formData.description?.trim()}
        className={`inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 ${
          (isSavingDraft || !formData.name?.trim() || !formData.title?.trim() || !formData.description?.trim()) ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        {isSavingDraft ? (
          <>
            <ButtonSpinner color="current" className="-ml-1 mr-1" />
            Saving...
          </>
        ) : (
          isEditingDraft ? 'Update Draft' : 'Save Draft'
        )}
      </button>
    );
  };

  // Render AI Generate button
  const renderAIButton = () => {
    if (isSettingsMode) return null;
    
    return (
      <button
        type="button"
        onClick={() => setIsAIModalOpen(true)}
        disabled={!formData.name?.trim()}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm font-medium text-primary-700 hover:bg-primary-100 dark:border-primary-900 dark:bg-primary-900/30 dark:text-primary-text-400 dark:hover:bg-primary-900/50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Sparkles className="h-4 w-4 flex-shrink-0" />
        <span className="whitespace-normal text-left">Generate with AI</span>
      </button>
    );
  };

  return (
    <div className={`flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 mt-8 w-full ${className}`}>
      {/* Left side - Previous button or Template button (desktop only) */}
      <div className="order-2 sm:order-1 hidden sm:block">
        {/* Introduction step has NO buttons on left side */}
        {currentStep === 0 ? (
          <div></div>
        ) : currentStep === 1 ? (
          // Internal Name page has Previous button then Template button on left (desktop)
          <div className="flex items-center gap-3">
            {renderPreviousButton()}
            {renderTemplateButton()}
          </div>
        ) : (
          // All other steps (2+) just have Previous button
          renderPreviousButton()
        )}
      </div>

      {/* Right side - Next/Submit buttons */}
      <div className="order-1 sm:order-2 w-full sm:w-auto">
        {/* Introduction step (Step 0) - ONLY show Get Started button */}
        {currentStep === 0 ? (
          !renderCustomButtons && (
            <button
              type="button"
              onClick={handleButtonClick}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600"
            >
              Get Started
              <ChevronRight className="h-4 w-4" />
            </button>
          )
        ) : currentStep === 1 ? (
          /* Internal Name page (Step 1) - Show Previous, AI, and Next buttons */
          <>
            {/* Mobile layout - show Template and Previous buttons */}
            <div className="sm:hidden w-full flex flex-col space-y-3 mb-4">
              {renderPreviousButton(true)}
              {renderTemplateButton(true)}
            </div>

            {/* Desktop and mobile buttons group */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
              {renderAIButton()}
              {renderNextButton()}
            </div>
          </>
        ) : (
          /* All other steps (2-5) - Show Save Draft, Previous, and Next/Create buttons */
          <>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
              {renderSaveDraftButton()}
              
              {/* Previous button (mobile only) */}
              <div className="sm:hidden">
                {renderPreviousButton(true)}
              </div>

              {renderNextButton()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}